import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../services/api';
import Button from '../ui/Button';
import { ErrorNotice, InfoNotice } from '../ui/States';
import { errorMessage } from '../../lib/programs';
import { FilePayload, downloadFile, when } from './logistics';

/** Download → send by email yourself → mark as sent (the next list shows only what changed). */
export function SendSteps({
  download,
  markSent,
  to,
  what,
  lastSent,
  onSent,
  downloadLabel = 'Download',
  primary = true,
}: {
  download: string;
  markSent: string;
  to: string;
  what: string;
  lastSent: string | null;
  onSent: () => void;
  /** The download button's words ("Download the list"). */
  downloadLabel?: string;
  /** False when another action is the screen's main one: the download is then a secondary button. */
  primary?: boolean;
}) {
  const [digest, setDigest] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const get = useMutation(async () => (await api.post(download)).data.data as FilePayload, {
    onSuccess: (file) => {
      downloadFile(file);
      setDigest(file.digest ?? null);
      setError(null);
    },
    onError: (err) => setError(errorMessage(err, 'Could not prepare the file.')),
  });
  const sent = useMutation(async () => (await api.post(markSent, { digest })).data.data, {
    onSuccess: () => {
      setDigest(null);
      setError(null);
      onSent();
    },
    onError: (err) => setError(errorMessage(err, 'Could not mark it sent.')),
  });
  const step = (n: number, done: boolean, text: ReactNode, action?: ReactNode) => (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-[10px] border-b border-line text-[15px] leading-[22px] text-ink">
      <span
        aria-hidden
        className={`flex-none w-6 h-6 rounded-full border text-center text-small font-semibold leading-[22px] ${
          done ? 'bg-accent border-accent text-surface' : 'border-line-strong text-ink-2'
        }`}
      >
        {done ? '✓' : n}
      </span>
      <span className="flex-[1_1_260px]">
        <span className="sr-only">{done ? 'Done: ' : `Step ${n}: `}</span>
        {text}
      </span>
      {action}
    </li>
  );
  return (
    <div>
      <ol>
        {step(
          1,
          !!digest || sent.isSuccess,
          <>Download the {what} (Excel).</>,
          <Button
            size="sm"
            variant={primary ? 'primary' : 'secondary'}
            disabled={get.isLoading}
            onClick={() => get.mutate()}
          >
            {get.isLoading ? 'Preparing…' : downloadLabel}
          </Button>,
        )}
        {step(
          2,
          sent.isSuccess,
          <>Email it to {to} yourself. The last tab lists what changed since the previous one.</>,
        )}
        {step(
          3,
          sent.isSuccess,
          <>Mark it as sent, so the next list shows only the changes.</>,
          <Button size="sm" disabled={!digest || sent.isLoading} onClick={() => sent.mutate()}>
            Mark as sent
          </Button>,
        )}
      </ol>
      <p className="text-small text-ink-3 mt-3">
        {lastSent ? `Last marked as sent ${when(lastSent)}.` : 'Never marked as sent.'}
        {!digest ? ' Mark as sent turns on after you download.' : ''}
      </p>
      {error ? (
        <div className="mt-2">
          <ErrorNotice>{error}</ErrorNotice>
        </div>
      ) : null}
      {sent.isSuccess ? (
        <div className="mt-2">
          <InfoNotice>Marked as sent.</InfoNotice>
        </div>
      ) : null}
    </div>
  );
}
